<script setup lang="ts">
import type { IntervalInputProps } from '.';

import { cn } from '@/utils';
import { useId } from 'vue';

import IconArrowLeftRight from '~icons/mdi/arrow-left-right';

import CurrencyInput from '../CurrencyInput/Input.vue';

const props = withDefaults(defineProps<IntervalInputProps>(), {
	updateOn: 'input'
});

const firstInputId = useId();
const secondInputId = useId();

const localMin = ref<number | null | undefined>(undefined);
const localMax = ref<number | null | undefined>(undefined);

const min = defineModel<number | null | undefined>('min', {
	default: () => null
});
const max = defineModel<number | null | undefined>('max', {
	default: () => null
});

const unit = computed(() => (props.unit === 'area' ? 'm²' : 'R$'));

const unitPosition = computed(() => (props.unit === 'area' ? 'right' : 'left'));

function validateAndUpdate(key: 'min' | 'max') {
	if (!localMin.value || !localMax.value) return;

	if (key === 'min') {
		if (localMin.value > localMax.value) {
			localMin.value = 0;
		}
	}

	if (key === 'max') {
		if (localMax.value < localMin.value) {
			localMax.value = 0;
		}
	}
}

function handleInput(key: 'min' | 'max') {
	if (props.updateOn !== 'input') return;

	validateAndUpdate(key);

	if (key === 'min') {
		min.value = localMin.value;
	}

	if (key === 'max') {
		max.value = localMax.value;
	}
}

function handleBlur() {
	if (min.value !== localMin.value) {
		validateAndUpdate('min');
		min.value = localMin.value;
	}

	if (max.value !== localMax.value) {
		validateAndUpdate('max');
		max.value = localMax.value;
	}
}

watch(
	min,
	newMin => {
		localMin.value = newMin;
	},
	{ immediate: true }
);

watch(
	max,
	newMax => {
		localMax.value = newMax;
	},
	{ immediate: true }
);
</script>

<template>
	<div :class="cn('flex w-full flex-col gap-5', props.class)">
		<h4
			class="flex w-full items-center font-matter-sq text-lg leading-[150%] tracking-[-0.36px] text-primary"
		>
			{{ title }}
		</h4>
		<form class="flex w-full items-center gap-2">
			<CurrencyInput
				:id="firstInputId"
				v-model="localMin"
				:placeholder="minLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="cn('', props.inputClasses)"
				@change="handleInput('min')"
				@blur="handleBlur"
			/>
			<IconArrowLeftRight class="!size-5 text-gray-400" />
			<CurrencyInput
				:id="secondInputId"
				v-model="localMax"
				:placeholder="maxLabel"
				:error="error"
				:affix="unit"
				:unit-position="unitPosition"
				:unit="unit"
				tabindex="0"
				class="flex-1"
				:class="cn('', props.inputClasses)"
				@change="handleInput('max')"
				@blur="handleBlur"
			/>
		</form>
	</div>
</template>
